import React, { memo, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import CheckBox from 'src/components/form/CheckBox';
import { Poppins } from 'src/common';
import _ from 'lodash';
import { Error } from '../';
import { Clear } from '@mui/icons-material';
import useOnClickOutside from 'src/hooks/useOnClickOutside';
import * as S from './MultiSelect.styles';

interface MultiSelectProps<T = string> {
  options: { label: string; value: T }[];
  value: T[];
  onChange: (value: T[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  dataCy?: string;
  className?: string;
  error?: string | false;
  createable?: {
    values: string[];
    onAdd: (value: string) => void;
    onClear: () => void;
    onRemove?: (value: string) => void;
  };
  selectedLabel?: string;
}

export const MultiSelect = memo(
  <T,>({
    options,
    onChange,
    onBlur,
    value,
    dataCy,
    placeholder = 'Select',
    className,
    error,
    createable,
    selectedLabel = 'selected',
  }: MultiSelectProps<T>) => {
    const colors = useTheme().colors;
    const [creatableText, setCreatableText] = useState('');
    const [creatableErr, setCreatableErr] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const ref = useRef(null);

    useOnClickOutside(ref, () => {
      setIsOpen(false);
    });

    useEffect(() => {
      if (createable) {
        const normalizeText = (text: string) => text.trim().replace(/\s+/g, ' ');
        const normalizedCreatableText = normalizeText(creatableText);
        const normalizedValues = createable.values.map(normalizeText);

        if (normalizedValues.includes(normalizedCreatableText)) {
          setCreatableErr('Value already exists');
        } else if (creatableText) {
          setCreatableErr(!creatableText.trim() ? 'Value cannot be empty' : '');
        } else {
          setCreatableErr('');
        }
      }
    }, [createable, creatableText]);

    const handleSelect = (key: T) => {
      let values: T[] = [];
      if (value.includes(key)) {
        values = value.filter((el) => el !== key);
      } else {
        values = [...value, key];
      }
      onChange(values);
    };

    const isAllSelected = options.every((el) => value.includes(el.value));
    const totalSelected = value.length + (createable?.values.length || 0);

    const renderClearButton = !!options.length || !!createable?.values.length;
    const canRenderDropdown = renderClearButton || creatableText;

    return (
      <>
        <S.RelativeWrap ref={ref}>
          <S.StyledInput
            className={className}
            value={creatableText}
            onChange={(e) => setCreatableText(e.target.value)}
            fullWidth
            error={!!creatableErr}
            helperText={creatableErr}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={totalSelected ? `${totalSelected} ${selectedLabel}` : placeholder}
            onFocus={() => setIsOpen(true)}
            // disabled={!createable}
            onBlur={() => {
              onBlur && onBlur();
            }}
            data-cy={dataCy}
            InputProps={{
              endAdornment:
                !!options.length || !!createable?.values.length ? (
                  <S.StyledInputAdornment position="end" onClick={() => setIsOpen(!isOpen)}>
                    <S.ArrowSvg
                      height="20"
                      width="20"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                      focusable="false"
                      $isOpen={isOpen}
                    >
                      <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                    </S.ArrowSvg>
                  </S.StyledInputAdornment>
                ) : undefined,
              autoComplete: 'off',
            }}
          />
          {isOpen && canRenderDropdown && (
            <S.Menux className="styled-scroll styled-scroll--themed">
              <div className="ms-options" onClick={(e) => e.stopPropagation()}>
                {creatableText && !creatableErr && (
                  <>
                    <div
                      className="ms-options__item"
                      // onClick={() => onChange(isAllSelected ? [] : options.map((el) => el.value))}
                      onClick={() => {
                        createable?.onAdd(creatableText.trim());
                        setCreatableText('');
                      }}
                    >
                      <S.AddOptionText nowrap>Add "{creatableText}"</S.AddOptionText>
                      <S.AddOptionIcon
                        xmlns="http://www.w3.org/2000/svg"
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        onClick={() => {
                          createable?.onAdd(creatableText.trim());
                          setCreatableText('');
                        }}
                      >
                        <path
                          d="M16.2792 8.93661L16.2628 7.211L9.42151 7.14586L9.35637 0.304613L7.63076 0.288183L7.6959 7.12943L0.854659 7.06429L0.87109 8.78989L7.71233 8.85504L7.77747 15.6963L9.50308 15.7127L9.43794 8.87147L16.2792 8.93661Z"
                          fill="url(#paint0_linear_8384_11057)"
                        />
                        <defs>
                          <linearGradient
                            id="paint0_linear_8384_11057"
                            x1="12.8096"
                            y1="3.75781"
                            x2="4.32428"
                            y2="12.2431"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor={colors.gp1} />
                            <stop offset="1" stopColor={colors.gp2} />
                          </linearGradient>
                        </defs>
                      </S.AddOptionIcon>
                    </div>
                    {renderClearButton && <div className="divider" />}
                  </>
                )}

                {!!options.length && (
                  <div
                    className="ms-options__item"
                    onClick={() => onChange(isAllSelected ? [] : options.map((el) => el.value))}
                  >
                    <CheckBox isChecked={isAllSelected} small />
                    <Poppins className="ms-options__item__title" nowrap>
                      All
                    </Poppins>
                  </div>
                )}

                {_.orderBy(options, [(el) => el.label.toLowerCase()]).map((el) => (
                  <div
                    className="ms-options__item"
                    data-cy={`gt-csa-${el.value}`}
                    key={el.value as string}
                    onClick={() => handleSelect(el.value)}
                  >
                    <CheckBox isChecked={value.includes(el.value)} small />
                    <Poppins className="ms-options__item__title" nowrap>
                      {el.label}
                    </Poppins>
                  </div>
                ))}

                {!!createable && (
                  <>
                    <div />
                    {createable.values.map((el) => (
                      <div
                        className="ms-options__item ms-options__item--creatable"
                        data-cy={`gt-csa-${el}`}
                        key={`other-${el}`}
                      >
                        <Poppins className="ms-options__item__title" nowrap>
                          {el}
                        </Poppins>
                        {createable.onRemove && (
                          <div
                            className="msc-delete"
                            onClick={() => (createable?.onRemove ? createable?.onRemove(el) : undefined)}
                          >
                            <Clear fontSize="small" />
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {renderClearButton && (
                  <div
                    className="ms-options__clear"
                    onClick={() => {
                      onChange([]);
                      createable?.onClear();
                    }}
                  >
                    <Poppins>Clear all</Poppins>
                  </div>
                )}
              </div>
            </S.Menux>
          )}
        </S.RelativeWrap>
        {error && <Error>{error}</Error>}
      </>
    );
  },
  (prev, next) => _.isEqual(prev, next),
) as <T>(props: MultiSelectProps<T>) => JSX.Element;
